import { useEffect, useState } from "react";

import {
  persistProjectTaskViewPreference,
  resolveProjectTaskViewPreference,
  serializeProjectTaskViewHistoryEntry,
  shouldPersistProjectTaskViewRoute,
} from "~/views/projectTaskViewPreference";
import type { DataViewRouteState } from "~/views/viewController";
import type { DataViewState } from "~/views/viewDefinition";
import {
  parseProjectTaskDataViewSearch,
  serializeProjectTaskDataViewSearch,
} from "~/views/viewUrlState";

function browserStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function locationSearch(): Record<string, string> {
  return Object.fromEntries(new URLSearchParams(window.location.search));
}

function applyTaskViewSearch(url: URL, search: Record<string, string | undefined>): void {
  for (const [key, value] of Object.entries(search)) {
    if (value === undefined) url.searchParams.delete(key);
    else url.searchParams.set(key, value);
  }
}

function readProjectTaskViewRouteFromLocation(projectId: string): DataViewRouteState {
  if (typeof window === "undefined") return parseProjectTaskDataViewSearch({});
  return resolveProjectTaskViewPreference(browserStorage(), projectId, locationSearch());
}

/**
 * Keeps shareable task-view URL state authoritative while retaining the last
 * complete project-scoped view as a local fallback for bare project routes.
 */
export function useProjectTaskViewRoute(
  projectId: string,
): readonly [
  DataViewRouteState,
  (
    state: DataViewState,
    savedViewId: string | null,
    previousState: DataViewState,
    previousSavedViewId: string | null,
  ) => void,
] {
  const [route, setRoute] = useState<DataViewRouteState>(() =>
    readProjectTaskViewRouteFromLocation(projectId),
  );

  useEffect(() => {
    const syncFromHistory = () => setRoute(readProjectTaskViewRouteFromLocation(projectId));
    syncFromHistory();
    window.addEventListener("popstate", syncFromHistory);
    return () => window.removeEventListener("popstate", syncFromHistory);
  }, [projectId]);

  useEffect(() => {
    if (!shouldPersistProjectTaskViewRoute(route)) return;
    persistProjectTaskViewPreference(
      browserStorage(),
      projectId,
      route.state,
      route.savedViewId || null,
    );
  }, [projectId, route]);

  const updateRoute = (
    state: DataViewState,
    savedViewId: string | null,
    previousState: DataViewState,
    previousSavedViewId: string | null,
  ) => {
    const url = new URL(window.location.href);
    if (route.hasExplicitState !== true && route.savedViewId.length === 0) {
      const currentUrl = new URL(url);
      applyTaskViewSearch(
        currentUrl,
        serializeProjectTaskViewHistoryEntry(previousState, previousSavedViewId),
      );
      window.history.replaceState(window.history.state, "", currentUrl);
    }
    const next = serializeProjectTaskDataViewSearch(state, savedViewId);
    applyTaskViewSearch(url, next);
    window.history.pushState(null, "", url);
    persistProjectTaskViewPreference(browserStorage(), projectId, state, savedViewId);
    setRoute({ state, savedViewId: savedViewId ?? "", hasExplicitState: true });
  };

  return [route, updateRoute] as const;
}
